import React, { useEffect, useState, useRef, useCallback } from "react";
import "./PostCard.css";
import {
  doc,
  getDoc,
  updateDoc,
  increment,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import CommentSection from "./CommentSection";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faUserFriends } from "@fortawesome/free-solid-svg-icons";
import { recordPostView } from "../utils/postViewTracker";
import { saveToGallery, formatSaveMessage } from "../utils/saveToGallery";
import ZoomableImage from "../components/ZoomableImage";





const getAnonViewerId = () => {
  if (typeof window === "undefined") {
    return `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
  let id = localStorage.getItem("anonId");
  if (!id) {
    id = "anon-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("anonId", id);
  }
  return id;
};

const getAdminDisplayName = (profile = {}, fallbackUser = null) => {
  if (profile.firstInitialLastName || profile.firstName || profile.lastName) {
    return [
      profile.firstInitialLastName ||
        (profile.firstName && profile.lastName ? `${profile.firstName[0]}. ${profile.lastName}` : profile.displayId),
    ].filter(Boolean).join(" ");
  }
  return fallbackUser?.displayName || fallbackUser?.email || "Admin";
};

const PostCard = ({ post, showScore = true, anchorId, isAdmin = false }) => {
  const {
    text,
    topic,
    images = [],
    linkUrl,
    createdAt,
    userName = "Investigator",
    authorDisplayName = "",
    agency = "Agency",
    country = "",
    stateProvince = "",
    state = "",
    city = "",
    authorLocation = "",
  } = post;


	const [expanded, setExpanded] = useState(false);
	const [previewImg, setPreviewImg] = useState(null);
	const [liked, setLiked] = useState(false);
	const [followingList, setFollowingList] = useState([]);
	const user = auth.currentUser;
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [adminBusy, setAdminBusy] = useState("");
  const [adminProfile, setAdminProfile] = useState(null);
  const cardRef = useRef(null);
  const viewTimerRef = useRef(null);
  const viewCountedRef = useRef(false);

  const recordViewOnce = useCallback(async () => {
    if (!post?.id || viewCountedRef.current) return false;
    viewCountedRef.current = true;
    const viewerId = user?.uid || getAnonViewerId();
    try {
      await recordPostView(post.id, viewerId);
      return true;
    } catch (err) {
      console.error("Failed to record post view:", err);
      viewCountedRef.current = false;
      return false;
    }
  }, [post?.id, user?.uid]);


	// Handle LIKE toggle (like/unlike)
const handleLike = async () => {
  if (!user) return;
  const postRef = doc(db, "posts", post.id);
  try {
    if (liked) {
      setLiked(false);
      await updateDoc(postRef, {
        likes: increment(-1),
        likedBy: arrayRemove(user.uid),
      });
    } else {
      setLiked(true);
      await updateDoc(postRef, {
        likes: increment(1),
        likedBy: arrayUnion(user.uid),
      });
    }
  } catch (e) {
    setLiked((v) => !v);
  }
};


useEffect(() => {
  if (!auth.currentUser) return;
  const fetchFollowing = async () => {
    const userRef = doc(db, "users", auth.currentUser.uid);
    const snap = await getDoc(userRef);
    if (snap.exists() && snap.data().following) {
      setFollowingList(snap.data().following);
    }
  };
  fetchFollowing();
}, []);

useEffect(() => {
  if (!isAdmin || !user?.uid) return;
  const fetchAdminProfile = async () => {
    const snap = await getDoc(doc(db, "users", user.uid));
    if (snap.exists()) setAdminProfile(snap.data());
  };
  fetchAdminProfile();
}, [isAdmin, user?.uid]);

const isFollowing = (userId) =>
  followingList.some((u) => u.userId === userId);

const handleFollowToggle = async (userId, userName, locationLabel) => {
  const userRef = doc(db, "users", auth.currentUser.uid);
  const current = followingList;

  const isAlready = current.some((u) => u.userId === userId);
  const updated = isAlready
    ? current.filter((u) => u.userId !== userId)
    : [...current, { userId, userName, locationLabel }];

  setFollowingList(updated);

  await updateDoc(userRef, {
    following: isAlready
      ? arrayRemove({ userId, userName, locationLabel })
      : arrayUnion({ userId, userName, locationLabel }),
  });
};

const getAdminMeta = () => ({
  uid: user?.uid || "",
  name: getAdminDisplayName(adminProfile, user),
});

const handleAdminRemovePost = async () => {
  if (!isAdmin || !user?.uid || !post?.id) return;
  if (!window.confirm("Remove this post from The Wire?")) return;
  const admin = getAdminMeta();
  setAdminBusy("remove");
  try {
    await updateDoc(doc(db, "posts", post.id), {
      isHidden: true,
      hiddenBy: admin.uid,
      hiddenByName: admin.name,
      hiddenReason: "Removed by admin",
      hiddenAt: serverTimestamp(),
    });
  } catch (err) {
    console.error("Failed to remove post:", err);
    alert("Could not remove post.");
  } finally {
    setAdminBusy("");
  }
};

const handleAdminToggleComments = async () => {
  if (!isAdmin || !user?.uid || !post?.id) return;
  const nextLocked = post.commentsLocked !== true;
  const admin = getAdminMeta();
  setAdminBusy(nextLocked ? "lock" : "allow");
  try {
    await updateDoc(doc(db, "posts", post.id), nextLocked ? {
      commentsLocked: true,
      commentsLockedBy: admin.uid,
      commentsLockedByName: admin.name,
      commentsLockedAt: serverTimestamp(),
      commentsLockedReason: "Locked by admin",
    } : {
      commentsLocked: false,
      commentsLockedBy: "",
      commentsLockedByName: "",
      commentsLockedAt: null,
      commentsLockedReason: "",
    });
  } catch (err) {
    console.error("Failed to update comment lock:", err);
    alert("Could not update comment lock.");
  } finally {
    setAdminBusy("");
  }
};


useEffect(() => {
  if (!user) {
    setLiked(false);
    return;
  }
  if (post?.likedBy?.includes?.(user.uid)) {
    setLiked(true);
  } else {
    setLiked(false);
  }
}, [user?.uid, post?.likedBy]);

// Passive view tracking with IntersectionObserver (5s dwell)
useEffect(() => {
  const el = cardRef?.current;
  if (!el || !post?.id) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
        if (viewTimerRef.current || viewCountedRef.current) return;
        viewTimerRef.current = setTimeout(() => {
          viewTimerRef.current = null;
          if (viewCountedRef.current) return;
          recordViewOnce();
        }, 5000);
      } else {
        if (viewTimerRef.current) {
          clearTimeout(viewTimerRef.current);
          viewTimerRef.current = null;
        }
      }
    },
    { threshold: [0, 0.6, 1] }
  );

  observer.observe(el);
  return () => {
    if (viewTimerRef.current) {
      clearTimeout(viewTimerRef.current);
      viewTimerRef.current = null;
    }
    observer.disconnect();
  };
}, [post?.id, recordViewOnce]);




  const dt = createdAt?.toDate ? createdAt.toDate() : new Date();
  const timestamp = new Intl.DateTimeFormat(undefined, {
    year: '2-digit', month: 'numeric', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(dt);

  const safeUserName = (authorDisplayName || userName || "Investigator").trim();
  const locationLabel = (authorLocation || [country, stateProvince || state, city].filter(Boolean).join(", ") || agency || "").trim();
  const canFollow = Boolean(post.userId);
  const scoreValue = post.trendingScore ?? post.engagementScore;

  const renderIdentity = () => {
    return (
      <span className="author">
        {safeUserName}
        {locationLabel ? <span className="agency">{` (${locationLabel})`}</span> : null}
      </span>
    );
  };




  return (
    <div
      id={anchorId}
      ref={cardRef}
      className="post-card"
    >
    <div className="post-header">
  {renderIdentity()}
{topic && <div className="post-topic">{topic}</div>}
</div>

      <div className="post-body">
        <p className="post-text">
  {expanded || text.length < 150 ? text : `${text.slice(0, 150)}...`}
</p>

{(text && text.length > 150) && (
  <button
    className="view-more-btn"
    onClick={async () => {
      if (expanded) {
        setExpanded(false);
        return;
      }
      await recordViewOnce();
      setExpanded(true);
    }}
  >
    {expanded ? "Show less" : "Show more"}
  </button>
)}

  {images.length > 0 && (
  <div className="post-images">
    {images.map((img, idx) => (
      <img
        key={idx}
        src={img}
        alt="Post attachment"
        style={{ cursor: "pointer" }}
        onClick={() => {
          document.body.classList.add("modal-open"); // stop scroll + fix context
          setPreviewImg(img);                         //  open viewer
        }}
      />
    ))}
  </div>
)}


{previewImg &&
  ReactDOM.createPortal(
    <div
      className="image-preview-overlay"
      onMouseDown={(e) => {
        if (e.target.classList.contains("image-preview-overlay")) {
          setPreviewImg(null);
          document.body.classList.remove("modal-open");
        }
      }}
      >
      <div className="image-preview-modal">
        <ZoomableImage src={previewImg} alt="Full view" />
      <button
  className="download-btn"
  onClick={async (e) => {
    e.stopPropagation();
    try {
      const result = await saveToGallery(previewImg);
      alert(formatSaveMessage(result));
    } catch (err) {
      console.error("Failed to save image:", err);
      alert(" Could not save image.");
    }
  }}
>
   Download
</button>


        <button
          className="close-btn"
          onClick={(e) => {
            e.stopPropagation();
            setPreviewImg(null);
            document.body.classList.remove("modal-open");
          }}
        >
          X
        </button>
      </div>
    </div>,
    document.body
  )}


       {/*  Embedded preview instead of plain link */}
 {linkUrl && <EmbeddedPreview url={linkUrl} />}
      </div>


<div className="post-timestamp-row">{timestamp}</div>
<div className="post-actions-v2">
  <span className="timestamp">{timestamp}</span>
  {showScore && scoreValue !== undefined && (
    <span className="score-chip">{"\uD83D\uDD25"} {Math.max(0, Math.round(scoreValue))}</span>
  )}
  <span className="views-count-fixed"><span className="ic">{"\uD83D\uDD76\uFE0F"}</span> {post.views || 0}</span>
  <button
    className={`like-btn ${liked ? "liked" : ""}`}
    onClick={handleLike}
    aria-pressed={liked}
    aria-label={liked ? "Unlike" : "Like"}
    title={liked ? "Unlike" : "Like"}
  > {"\uD83D\uDC4D"} {post.likes || 0}
  </button>
  <button
    className="comment-btn"
    onClick={() => setShowCommentInput((v) => !v)}
    aria-expanded={showCommentInput}
    title={showCommentInput ? "Hide comments" : "Add a comment"}
  > {"\uD83D\uDCAC"} {post.commentCount || 0}
  </button>
  {auth.currentUser?.uid !== post.userId && canFollow && (
 <button
  className={`follow-icon ${isFollowing(post.userId) ? "following" : ""}`}
  onClick={() => handleFollowToggle(post.userId, safeUserName, locationLabel)}
  aria-label={isFollowing(post.userId) ? "Following" : "Follow"}
  title={isFollowing(post.userId) ? "Following" : "Follow"}
>
  {isFollowing(post.userId) ? (
    <FontAwesomeIcon icon={faUserFriends} className="person person-2" />
  ) : (
    <FontAwesomeIcon icon={faUser} className="person person-1" />
  )}
</button>


  )}
</div>

{isAdmin && (
  <div className="post-admin-actions">
    <button
      type="button"
      className="post-admin-button remove"
      disabled={Boolean(adminBusy)}
      onClick={handleAdminRemovePost}
      title="Remove post"
      aria-label="Remove post"
    >
      {adminBusy === "remove" ? "Removing..." : "Remove"}
    </button>
    <button
      type="button"
      className="post-admin-button lock"
      disabled={Boolean(adminBusy)}
      onClick={handleAdminToggleComments}
      title={post.commentsLocked ? "Allow comments" : "Lock comments"}
      aria-label={post.commentsLocked ? "Allow comments" : "Lock comments"}
    >
      {adminBusy === "lock"
        ? "Locking..."
        : adminBusy === "allow"
        ? "Allowing..."
        : post.commentsLocked
        ? "Allow Comments"
        : "Lock Comments"}
    </button>
  </div>
)}

<CommentSection
  postId={post.id}
  post={post}
  hideHeader
  showInput={showCommentInput}
  initiallyExpanded={false}
  onSubmitComplete={() => setShowCommentInput(false)}
  isAdmin={isAdmin}
 />



    </div>

  );
};
//  Embedded Link / Media Preview
const EmbeddedPreview = ({ url }) => {
  const lower = url.toLowerCase();

if (lower.includes("vimeo.com")) {
  const idMatch = lower.match(/vimeo\.com\/(\d+)/);
  const videoId = idMatch ? idMatch[1] : null;
  if (videoId) {
    return (
      <div className="embed-wrapper">
        <iframe
          src={`https://player.vimeo.com/video/${videoId}`}
          title="Vimeo video preview"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
    );
  }
}


	if (lower.endsWith(".mp4") || lower.endsWith(".mov") || lower.endsWith(".webm")) {
  return (
    <video className="embed-video" controls preload="metadata">
      <source src={url} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  );
}
  

// YouTube thumbnail preview (fallback-safe)
if (lower.includes("youtube.com") || lower.includes("youtu.be")) {
  let videoId = null;
  const vParam = lower.match(/[?&]v=([^&]+)/);
  if (vParam) videoId = vParam[1];
  if (!videoId) {
    const short = lower.match(/youtu\.be\/([^?]+)/);
    if (short) videoId = short[1];
  }

  if (videoId) {
    const thumbUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="video-preview-card"
      >
        <div className="video-thumb">
    <img
  src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
  alt="YouTube thumbnail"
  onError={(e) => {
    console.warn("🔸 Missing YouTube thumbnail:", videoId);
    e.target.onerror = null; // stop recursive triggers
    e.target.src = "/placeholder.jpg"; // points directly to /public/placeholder.jpg
    e.target.style.objectFit = "cover";
    e.target.style.background = "#000";
  }}
  style={{
    borderRadius: "8px",
    width: "100%",
    height: "auto",
    objectFit: "cover",
  }}
/>


          <div className="video-overlay">â–¶ Watch on YouTube</div>
        </div>
      </a>
    );
  }
}


	//  Instagram thumbnail preview
if (lower.includes("instagram.com")) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="video-preview-card"
    >
      <div className="video-thumb">
        <img
          src="https://www.instagram.com/static/images/ico/favicon-200.png/ab6eff595bb1.png"
          alt="Instagram"
        />
        <div className="video-overlay">ðŸ“¸ View on Instagram</div>
      </div>
    </a>
  );
}

  //  TikTok preview
  if (lower.includes("tiktok.com")) {
    return (
      <div className="embed-wrapper">
        <blockquote
          className="tiktok-embed"
          cite={url}
          style={{ maxWidth: "325px", minWidth: "220px" }}
        >
          <a href={url}></a>
        </blockquote>
        <script async src="https://www.tiktok.com/embed.js"></script>
      </div>
    );
  }

	// Direct video files (.mp4, .mov, .webm)
if (
  lower.endsWith(".mp4") ||
  lower.endsWith(".mov") ||
  lower.endsWith(".webm") ||
  lower.includes(".mp4?") ||
  lower.includes(".mov?") ||
  lower.includes(".webm?")
) {
  return (
    <div className="embed-wrapper">
      <video
        className="embed-video"
        controls
        preload="metadata"
        playsInline
        poster=""
      >
        <source
  src={url}
  type={
    lower.endsWith(".webm")
      ? "video/webm"
      : lower.endsWith(".mov")
      ? "video/quicktime"
      : "video/mp4"
  }
/>

        Your browser does not support the video tag.
      </video>
    </div>
  );
}


  // Generic link card
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="link-preview"
    >
      <div className="link-box">
        <div className="link-favicon">
          <img
            src={`https://www.google.com/s2/favicons?sz=64&domain_url=${url}`}
            alt="favicon"
          />
        </div>
        <div className="link-info">
          <p className="link-url">{url}</p>
          <p className="link-hint">Open link â†—</p>
        </div>
      </div>
    </a>
  );
};


export default PostCard;

